// tabs/ability-island.ts — the BG3-style center-top "ability island" on the action bar.
//
// A row of small category icons (one per ability category the PC POSSESSES, passive abilities excluded),
// and a fly-up strip of that category's usable rune medallions when one is picked. Order:
//   movement → NPC skill-ability groups → free → quick → ability → full → reaction → sanguine
//   → charged abilities                                                   (computed.creature.abilities)
//   → curses (computed.creature.castable.curses_known)
//   → mentas, split by level                                                 (computed.creature.mentas)
//   → spells, split by level (numbered, like the castable-spells filter)     (computed.creature.castable)
//
// Medallions reuse generateAbilityButton (the SAME runic medium-icons as the Abilities-tab icon view), so
// clicking one casts/uses it through the existing handlers. Pure render — the open-state + toggling/sort
// live in action-bar.ts (it owns repaintActionBars). Everything reads the blob synchronously.

import { casterCanUseOpportunityAttack, generateAbilityButton, renderSimpleTriggerAbilityButton } from '../ability-button';
import { abilityEntriesOfType, resolveAbilityNest, abilityEntryId, abilityEntryName, abilityEntryRequiresLevel, abilitySkillSource, requiredSkillTierEntries, ACTIONABLE_ABILITY_TYPES, iconPh, type AbilitySkillSource } from './ability-row';
import { renderEncounterPositionHtml, renderMovementActionStrip } from './movement';
import { num } from '../read-helpers';
import type { CachedCaster, CasterItemNode } from '../types';
import { hasCampaignPermission, hasPlayerPermission, plTotal } from '../../shared/array-types';
import { pageSummonInjectedCantrips } from '../../shared/summon-cantrips-browser';
import { renderPilotedAbilityRefusal } from '../piloted-ability-refusal';

export interface IslandOpen { key: string; level: number | null; sort: 'alpha' | 'level'; skillSourceId?: string | null; }
interface IslandItem { nest: CasterItemNode; name: string; level: number; spellType: string | null; source: string | null; consumeNested?: boolean; skillSource?: AbilitySkillSource | null; skillTier?: number; powerLevel?: number; }
interface IslandCat { key: string; level: number | null; icon: string; label: string; }

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function lv(nest: unknown, k: string): number {
    return num(ftLayerValue(nest, k), 0);
}

// ── per-category medallion-source lists (all synchronous off the blob) ──
function abilityTypeItems(caster: CachedCaster, type: string): IslandItem[] {
    const out: IslandItem[] = [];
    for (const e of abilityEntriesOfType(caster, type)) {
        const nest = recordOf(resolveAbilityNest(e, caster));
        if (!nest?.item_layer_types) continue; // skip leaned shells
        const entry = recordOf(e);
        out.push({ nest, name: abilityEntryName(e), level: num(abilityEntryRequiresLevel(e), 0), spellType: null, source: typeof entry?.source === 'string' ? entry.source : null, consumeNested: entry?.consume_nested_unit_per_use === true, skillSource: abilitySkillSource(caster, e), powerLevel: plTotal(nest) });
    }
    return out;
}

function isOrdinaryEncounterCreature(caster: CachedCaster): boolean {
    const encounterCreatureId = String(caster.encounter_creatures?.encounter_creature_id ?? '').trim();
    const playerCharacterId = String(caster.player_characters?.item_id ?? '').trim();
    const casterType = String(caster.encounter_creatures?.item_type ?? ftItemsCol(caster, 'item_type') ?? '').trim().toLowerCase();
    return encounterCreatureId !== '' && playerCharacterId === '' && casterType !== 'lair';
}

/** The tier at which this ability is acquired from its owning skill. Prefer gates that name the
 *  resolved source skill; malformed legacy entries fall back to their highest authored skill gate. */
function acquiredSkillTier(nest: unknown, skillSource: AbilitySkillSource): number {
    const entries = requiredSkillTierEntries(nest);
    const sourceId = skillSource.id.toUpperCase();
    const matching = entries.filter(({ entry }) => abilityEntryId(entry).toUpperCase() === sourceId);
    const relevant = matching.length > 0 ? matching : entries;
    if (relevant.length === 0) return Number.POSITIVE_INFINITY;
    return Math.max(...relevant.map(({ tier }) => tier));
}

/** All active skill-granted actions on an ordinary NPC, deduped across their action-economy buckets. */
function npcSkillAbilityItems(caster: CachedCaster): IslandItem[] {
    if (!isOrdinaryEncounterCreature(caster)) return [];
    const out: IslandItem[] = [];
    const seen = new Set<string>();
    for (const type of ACTIONABLE_ABILITY_TYPES) {
        for (const entry of abilityEntriesOfType(caster, type)) {
            const skillSource = abilitySkillSource(caster, entry);
            if (!skillSource) continue;
            const abilityId = abilityEntryId(entry).toUpperCase();
            if (abilityId === '' || seen.has(abilityId)) continue;
            const nest = recordOf(resolveAbilityNest(entry, caster));
            if (!nest?.item_layer_types) continue;
            seen.add(abilityId);
            const entryRecord = recordOf(entry);
            out.push({
                nest,
                name: abilityEntryName(entry),
                level: num(abilityEntryRequiresLevel(entry), 0),
                spellType: null,
                source: typeof entryRecord?.source === 'string' ? entryRecord.source : null,
                consumeNested: entryRecord?.consume_nested_unit_per_use === true,
                skillSource,
                skillTier: acquiredSkillTier(nest, skillSource),
                powerLevel: plTotal(nest),
            });
        }
    }
    return out;
}

/** Spell/curse/menta pools are lean rows ({item_id, name, level, spell_type, source}); resolve each to its
 *  full nested def (ftFindFullNestedItem) so generateAbilityButton can read its layers. levelKey picks the
 *  level layer (spell_level for spells/curses, menta_level for mentas). */
function poolItems(_caster: CachedCaster, pool: unknown, levelKey: string): IslandItem[] {
    const finder = window.ftFindFullNestedItem;
    const out: IslandItem[] = [];
    if (!Array.isArray(pool)) return out;
    for (const raw of pool) {
        const s = recordOf(raw);
        if (!s) continue;
        const id = String(s.item_id ?? s.id ?? recordOf(s.items)?.item_id ?? '');
        let nest = recordOf(s.nest);
        if (!nest?.item_layer_types) nest = null;
        if (!nest && id && typeof finder === 'function') nest = recordOf(finder(id));
        const nestRecord = recordOf(nest);
        if (!nestRecord?.item_layer_types) continue;
        out.push({
            nest: nestRecord,
            name: String(s.name ?? ftItemsCol(nestRecord, 'item_name') ?? ''),
            level: num(s.level ?? lv(nestRecord, levelKey), 0),
            spellType: typeof s.spell_type === 'string' ? s.spell_type : null,
            source: typeof s.source === 'string' ? s.source : null,
        });
    }
    return out;
}

function spellsPool(caster: CachedCaster): unknown {
    // summons_gain_caster_cantrips (Wizard v9 Death-Linked Familiar): a summon whose controller provides it lists the controller's
    // cantrips too — plain rows (no spell_type / source marker); the cast lane rolls them with the controller's stats.
    const injected = pageSummonInjectedCantrips(caster as unknown as Record<string, unknown>);
    const castable = recordOf(caster.computed?.creature?.castable);
    if (!castable) return injected;
    const known = Array.isArray(castable.spells_known) ? castable.spells_known : [];
    // Innate and at-will spells are castable actions too. Leaving them out of the
    // island hid real NPC spells such as Summon Dancing Light even though Janus
    // compiled the grant and the creature's spell sheet listed it.
    const granted = ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell']
        .flatMap(type => (Array.isArray(castable[type]) ? castable[type] : [])
            .map(raw => ({ ...recordOf(raw), spell_type: type })));
    return [...known, ...granted, ...injected];
}
function cursesPool(caster: CachedCaster): unknown { return caster.computed?.creature?.castable?.curses_known ?? caster.computed?.creature?.curses_known ?? []; }

/** Mentas live in computed.creature.abilities.menta (relocated there — see _creature_console_tabs_inc.php),
 *  so they come through abilityEntriesOfType like the other ability types; the menta LEVEL is the
 *  menta_level layer on the resolved nest (the same field the Mentas tab groups by). */
function mentaItems(caster: CachedCaster): IslandItem[] {
    const out: IslandItem[] = [];
    for (const e of abilityEntriesOfType(caster, 'menta')) {
        const nest = recordOf(resolveAbilityNest(e, caster));
        if (!nest?.item_layer_types) continue;
        out.push({ nest, name: abilityEntryName(e), level: lv(nest, 'menta_level'), spellType: null, source: null });
    }
    return out;
}

function sortedLevels(items: IslandItem[]): number[] {
    return Array.from(new Set(items.map((i) => i.level))).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
}

const DEMO_GENERAL_ABILITIES = [
    ['B2D3417B-2700-11CB-E17D-38DECAF66F1A', 'spot_hidden_creatures', 'Spot Hidden Creatures'],
    ['5B518ED7-4CEF-72BE-F20B-912598267714', 'search_for_hidden_inventory', 'Search for Hidden Inventory'],
    ['B2BC3219-C745-7050-51DD-892951F0026B', 'resolve', 'Resolve'],
    ['28D6D12E-A33F-4D3F-A668-C0C9037F5BC1', 'snap_outta_it', 'Snap Out of It'],
    ['904F852B-443B-24E4-7B71-F785BB66433B', 'stop_the_bleeding', 'Stop Bleeding'],
] as const;
const RESOLVE_ABILITY_ID = 'B2BC3219-C745-7050-51DD-892951F0026B';
const DEMO_GENERAL_ATTACKS = [
    ['D1EADC34-832C-1909-2FC3-722AC857FBEA', 'opportunity_attack', 'Opportunity Attack'],
    ['9AE1980E-783E-16F5-E8C4-904435FC0A69', 'shove', 'Shove'],
    ['6D6CCCC6-01F5-965C-FCF4-DBDF4DBCCD93', 'grapple', 'Grapple'],
    ['C6FD9254-7968-86FC-5FDC-8E87914DEDE4', 'disarm', 'Disarm'],
] as const;

// Equipment slots whose occupant is "in hand" for a throw (mirror of EquipmentSlot::LIST — inventory.ts SLOT).
// The tail slot is included so a tail-held object gets its throw the day tail wielding lands.
const THROW_HELD_SLOTS: ReadonlyArray<readonly [string, string]> = [
    ['CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD', 'Main hand'],
    ['E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D', 'Both hands'],
    ['4FF85144-67EC-9D87-AE28-15137A1A2481', 'Off hand'],
    ['16ABAE95-5D18-474D-ADFF-8C20FC2439F1', 'Tail'],
];
// Twin of throw-object.ts NON_THROWABLE_ITEM_TYPES (the server's non-carryable set + map-only types).
const NON_THROWABLE_ITEM_TYPES = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor',
    'vehicle_land', 'vehicle_water', 'vehicle_air', 'war_machine', 'ore_vein', 'tree', 'trap', 'digsite', 'map_marker', 'wall_run']);

function scalarText(value: unknown): string {
    return String((Array.isArray(value) ? value[0] : value) ?? '').trim();
}

/** One Throw button per held item (main / both / off hand, tail), so a throw never needs an inventory rummage.
 *  A throwing weapon throws as a weapon (its own attack, straight flight); anything else goes through the
 *  generic weight-ranged Throw Object lane. Janus re-gates both; the object plan only pre-greys the button. */
function renderHeldThrowButtons(caster: CachedCaster, casterId: string): string {
    const inventory = Array.isArray(caster.inventory) ? caster.inventory : [];
    const planFor = (window as Window & { ftThrowObjectPlan?: (id: string) => { ok: boolean; reason: string | null; rangeFt: number | null; twoHanded: boolean } }).ftThrowObjectPlan;
    let html = '';
    for (const [slotId, slotLabel] of THROW_HELD_SLOTS) {
        for (const raw of inventory) {
            const row = recordOf(raw);
            if (!row || scalarText(ftInventoryField(row, 'equipped')).toUpperCase() !== slotId) continue;
            const invId = scalarText(ftInventoryField(row, 'inventory_id'));
            const type = scalarText(ftItemsCol(row, 'item_type'));
            if (!invId || NON_THROWABLE_ITEM_TYPES.has(type)) continue;
            const name = scalarText(ftInventoryField(row, 'inventory_name')) || scalarText(ftItemsCol(row, 'item_name')) || 'item';
            const asWeapon = type === 'weapon' && num(ftLayerValue(row, 'item_throwable'), 0) === 1;
            const plan = asWeapon ? null : planFor?.(invId);
            const disabled = !!plan && !plan.ok;
            let thumb = typeof window.renderInventoryThumbnail === 'function'
                ? window.renderInventoryThumbnail(row, {}, {
                    flow_layout: true, show_overlay: false, use_size_based_width: false,
                    thumbnail_width: 30, thumbnail_height: 30, is_draggable: false, show_log_message: 0, show_icons: false,
                    dom_id: false, exclude_dropzone: true,
                })
                : '';
            // Inert decoration: the button owns the click, so the thumbnail must not read as a map target.
            thumb = thumb.replace(/\sdata-inventory_id="[^"]*"/g, '');
            const picture = `<span class="ft-demo-throw-held__item">${thumb || iconMap('inventory', 28, 28, false)}</span>`;
            // A throwing weapon gets Throw (straight) + Lob (arc over cover); an object lobs by default and
            // its placement's L key flips it straight, so it needs only the one button.
            const flights: Array<'direct' | 'lob' | null> = asWeapon ? ['direct', 'lob'] : [null];
            for (const flight of flights) {
                const detail = flight === 'direct' ? 'straight weapon throw'
                    : flight === 'lob' ? 'lobbed over cover — a ceiling still blocks'
                    : plan?.ok && plan.rangeFt !== null ? `${plan.rangeFt} ft${plan.twoHanded ? ' · both hands' : ''}` : (plan?.reason ?? '');
                const title = `${flight === 'lob' ? 'Lob' : 'Throw'} ${name} (${slotLabel}${detail ? ` · ${detail}` : ''})`;
                html += `<button type="button" class="ft-demo-island-action ft-demo-throw-held" data-ft-ab-action="general-throw"`
                    + ` data-ft-caster-id="${escapeHtml(casterId)}" data-ft-inventory-id="${escapeHtml(invId)}" data-ft-throw-kind="${asWeapon ? 'weapon' : 'object'}"`
                    + (flight ? ` data-ft-throw-flight="${flight}"` : '')
                    + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false"`
                    + ` data-bs-original-title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}"${disabled ? ' disabled aria-disabled="true"' : ''}>`
                    + picture
                    + (flight === 'lob' ? '<b class="ft-demo-throw-held__flight">Lob</b>' : '')
                    + `<span class="ft-demo-throw-held__glyph">${iconMap(asWeapon ? 'throw_weapon' : 'throw_inventory', 16, 16, false)}</span></button>`;
            }
        }
    }
    return html;
}

function creatureSheetDemoEnabled(): boolean {
    return !!document.querySelector('[data-ft3d-creature-sheet-demo="1"]');
}

function hasActionBarCreatureGmTools(caster: CachedCaster): boolean {
    const encounterCreatureId = String(caster.encounter_creatures?.encounter_creature_id ?? '').trim();
    return encounterCreatureId !== ''
        && (hasPlayerPermission(caster) || hasCampaignPermission(caster))
        && typeof window.renderActionBarCreatureGmControls === 'function';
}

function demoActionButton(casterId: string, abilityId: string, icon: string, label: string, disabled = false): string {
    return `<button type="button" class="ft-demo-island-action" data-ft-ab-action="single-ability"`
        + ` data-ft-caster-id="${escapeHtml(casterId)}" data-ft-ability-id="${abilityId}"`
        + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false"`
        + ` data-bs-original-title="${escapeHtml(label)}"${disabled ? ' disabled aria-disabled="true"' : ''}>`
        + `${iconMap(icon, 28, 28, false)}</button>`;
}

function renderDemoGeneralAbilities(caster: CachedCaster, casterId: string): string {
    const buttons = DEMO_GENERAL_ABILITIES
        .map(([id, icon, label]) => {
            if (id === RESOLVE_ABILITY_ID) {
                return renderSimpleTriggerAbilityButton(id, 'ft-demo-island-action', iconMap(icon, 28, 28, false), caster, {
                    title: label,
                    extraAttrs: { 'data-ft-info-item-id': id },
                });
            }
            return demoActionButton(casterId, id, icon, label);
        }).join('');
    return `<div class="ft-ability-flyout__inner"><div class="ft-ability-flyout__head">`
        + `<span class="ft-ability-flyout__title">General Abilities</span></div>`
        + `<div class="ft-demo-island-strip">${buttons}</div></div>`;
}

function renderDemoGeneralAttacks(caster: CachedCaster, casterId: string): string {
    const encounter = recordOf(caster.encounter_creatures) ?? {};
    const attacks = recordOf(caster.computed?.creature?.attacks);
    const extraRaw = attacks?.extra_attack;
    const extra = num(recordOf(extraRaw)?.total ?? extraRaw, 0);
    const headWeapon = num(ftLayerValue(caster, 'count_head_weapon_equipped'), 0) > 0;
    const adjuster = (kind: 'weapon' | 'head', icon: string, value: unknown, label: string): string => (
        `<div class="ft-demo-attack-adjust" role="group" aria-label="${label}">`
        + `<button type="button" data-ft-ab-action="adjust-${kind}-attack" data-ft-delta="-1" aria-label="Reduce ${label}">&minus;</button>`
        + `<span>${iconMap(icon, 24, 24, false)}<b>${num(value, 0)}</b></span>`
        + `<button type="button" data-ft-ab-action="adjust-${kind}-attack" data-ft-delta="1" aria-label="Add ${label}">&plus;</button></div>`
    );
    let budgets = adjuster('weapon', 'weapon_attack_count', encounter.weapon_attack, 'weapon attacks');
    budgets += `<button type="button" class="ft-demo-attack-info" data-ft-pc-desc-action="attacks-info"`
        + ` data-ft-caster-id="${escapeHtml(casterId)}" title="Extra attacks — where they come from">`
        + `${iconMap('extra_attack', 24, 24, false)}<b>${extra}</b></button>`;
    if (headWeapon) budgets += adjuster('head', 'head_attack', encounter.head_attack, 'head attacks');
    const oaUsable = casterCanUseOpportunityAttack(caster);
    const actions = DEMO_GENERAL_ATTACKS.map(([id, icon, label], index) => {
        if (index === 0) {
            return renderSimpleTriggerAbilityButton(id, 'ft-demo-island-action', iconMap(icon, 28, 28, false), caster, {
                title: oaUsable ? label : `${label} (requires a melee or natural weapon)`,
                isDisabled: !oaUsable,
            });
        }
        return demoActionButton(casterId, id, icon, label);
    }).join('') + renderHeldThrowButtons(caster, casterId);
    return `<div class="ft-ability-flyout__inner"><div class="ft-ability-flyout__head">`
        + `<span class="ft-ability-flyout__title">General Attacks</span></div>`
        + `<div class="ft-demo-general-attacks"><div class="ft-demo-attack-budgets">${budgets}</div>`
        + `<div class="ft-demo-island-strip">${actions}</div></div></div>`;
}

/** The ordered, possessed-only category list. */
export function islandCategories(caster: CachedCaster, spellsOnly = false): IslandCat[] {
    if (spellsOnly) return sortedLevels(poolItems(caster, spellsPool(caster), 'spell_level'))
        .map(level => ({ key: 'spell', level, icon: 'spell', label: `Spells · Level ${level}` }));
    const cats: IslandCat[] = [];
    // Movement category FIRST (ahead of free abilities): its fly-up is the preferred-speed selector +
    // stance/mount/vehicle actions (renderMovementActionStrip), not ability medallions.
    if (renderMovementActionStrip(caster) !== '') cats.push({ key: 'movement', level: null, icon: 'movement', label: 'Movement' });
    if (creatureSheetDemoEnabled()) {
        cats.push({ key: 'general_abilities', level: null, icon: 'general_ability', label: 'General Abilities' });
        cats.push({ key: 'general_attacks', level: null, icon: 'unhindered_attacks', label: 'General Attacks' });
    }
    if (npcSkillAbilityItems(caster).length > 0) cats.push({ key: 'skill_abilities', level: null, icon: 'skill', label: 'Skill Abilities' });
    const addType = (type: string, label: string) => {
        if (abilityTypeItems(caster, type).length > 0) cats.push({ key: type, level: null, icon: type, label });
    };
    addType('free_ability', 'Free Abilities');
    addType('quick_ability', 'Quick Abilities');
    addType('ability', 'Abilities');
    addType('full_ability', 'Full Abilities');
    addType('reaction_ability', 'Reaction Abilities');
    addType('sanguine', 'Sanguine');
    addType('charged_ability', 'Charged Abilities');
    if (poolItems(caster, cursesPool(caster), 'spell_level').length > 0) cats.push({ key: 'curse', level: null, icon: 'curse', label: 'Curses' });
    for (const l of sortedLevels(mentaItems(caster))) cats.push({ key: 'menta', level: l, icon: 'menta', label: `Mentas · Level ${l}` });
    for (const l of sortedLevels(poolItems(caster, spellsPool(caster), 'spell_level'))) cats.push({ key: 'spell', level: l, icon: 'spell', label: `Spells · Level ${l}` });
    // Last by design: destructive/administrative controls live at the far-right edge of the
    // action island and are emitted only for a viewer with explicit creature/campaign permission.
    if (creatureSheetDemoEnabled() && hasActionBarCreatureGmTools(caster)) {
        cats.push({ key: 'gm_tools', level: null, icon: 'game_master_tool', label: 'GM Tools' });
    }
    return cats;
}

function itemsForCat(caster: CachedCaster, cat: IslandCat): IslandItem[] {
    switch (cat.key) {
        case 'skill_abilities': return npcSkillAbilityItems(caster);
        case 'curse': return poolItems(caster, cursesPool(caster), 'spell_level');
        case 'menta': return mentaItems(caster).filter((i) => i.level === cat.level);
        case 'spell': return poolItems(caster, spellsPool(caster), 'spell_level').filter((i) => i.level === cat.level);
        default: return abilityTypeItems(caster, cat.key);
    }
}

function catMatches(c: IslandCat, open: IslandOpen): boolean {
    return c.key === open.key && (c.level ?? null) === (open.level ?? null);
}

/** Which mobile TAB-GROUP a category belongs to (owner 2026-06-27): spells / curses / mentas each get their OWN
 *  tab when present; movement + the action-economy ability types go under "Abilities". Drives the small-screen
 *  tab filtering in action-bar.ts (data-ft-island-group). */
export function islandCatGroupForKey(key: string): 'abilities' | 'spells' | 'curses' | 'mentas' {
    if (key === 'spell') return 'spells';
    if (key === 'curse') return 'curses';
    if (key === 'menta') return 'mentas';
    return 'abilities';
}

/** The set of island tab-groups the caster actually possesses (so action-bar shows only the tabs that have
 *  content). e.g. {'abilities','spells'} for a wizard with no curses/mentas. */
export function islandGroupsPresent(caster: CachedCaster): Set<'abilities' | 'spells' | 'curses' | 'mentas'> {
    return new Set(islandCategories(caster).map((c) => islandCatGroupForKey(c.key)));
}

/** The category-icon row (center island). '' when the PC possesses none of these categories. */
export function renderAbilityIslandIcons(caster: CachedCaster, casterId: string, open: IslandOpen | undefined, spellsOnly = false): string {
    const cats = islandCategories(caster, spellsOnly);
    const refusal = spellsOnly ? '' : renderPilotedAbilityRefusal(caster);
    if (cats.length === 0) return refusal;
    const cid = escapeHtml(casterId);
    let html = refusal + '<div class="ft-action-island">';
    for (const c of cats) {
        const active = !!open && catMatches(c, open);
        const lvlBadge = c.level !== null ? `<span class="ft-island-lvl">${c.level}</span>` : '';
        html += `<button type="button" class="ft-island-cat${active ? ' is-active' : ''}"`
            + ` data-ft-island-group="${islandCatGroupForKey(c.key)}" data-ft-island-key="${escapeHtml(c.key)}"`
            + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(c.label)}"`
            + ` onclick="ftActionBarSelectAbilityCategory('${cid}','${escapeHtml(c.key)}',${c.level === null ? 'null' : c.level})">`
            + `${iconPh(c.icon, 22, 22, false)}${lvlBadge}</button>`;
    }
    return html + '</div>';
}

/** The fly-up strip of usable medallions for the currently-open category. '' when nothing is open. */
export function renderAbilityFlyout(caster: CachedCaster, casterId: string, open: IslandOpen | undefined): string {
    if (!open) return '';
    const cat = islandCategories(caster, open.key === 'spell').find((c) => catMatches(c, open));
    if (!cat) return '';
    if (cat.key === 'general_abilities') return renderDemoGeneralAbilities(caster, casterId);
    if (cat.key === 'general_attacks') return renderDemoGeneralAttacks(caster, casterId);
    if (cat.key === 'gm_tools') {
        const controls = window.renderActionBarCreatureGmControls?.(casterId, caster) ?? '';
        if (controls === '') return '';
        return `<div class="ft-ability-flyout__inner ft-actionbar-gm-tools">`
            + `<div class="ft-ability-flyout__head"><span class="ft-ability-flyout__title">GM Tools</span></div>`
            + controls + `</div>`;
    }
    // Movement is a special category: its fly-up is movement-action BUTTONS (speed selector + stance/
    // mount), not ability medallions, so it skips the sort toggle.
    if (cat.key === 'movement') {
        const strip = renderMovementActionStrip(caster);
        if (strip === '') return '';
        // Query-only creature-sheet review: consolidate the read-only persisted X/Y/Z receipt beside
        // the canonical movement controls. Story membership remains derived per docs/FT3D_FLOORS.md;
        // this renders no story field and owns no write path.
        const demoPosition = !!document.querySelector('[data-ft3d-creature-sheet-demo="1"]');
        const position = demoPosition
            ? renderEncounterPositionHtml(caster, true)
            : '';
        const actions = demoPosition
            ? `<div class="ft-movement-strip__actions">${strip}</div>`
            : strip;
        const header = `<div class="ft-ability-flyout__head"><span class="ft-ability-flyout__title">${escapeHtml(cat.label)}</span></div>`;
        return `<div class="ft-ability-flyout__inner">${header}<div class="ft-ability-flyout__strip ft-movement-strip">${actions}${position}</div></div>`;
    }
    if (cat.key === 'skill_abilities') {
        const grouped = new Map<string, { source: AbilitySkillSource; items: IslandItem[] }>();
        for (const item of itemsForCat(caster, cat)) {
            const source = item.skillSource ?? { id: 'skill', name: 'Skill', type: 'skill' };
            const key = source.id.toUpperCase();
            const group = grouped.get(key) ?? { source, items: [] };
            group.items.push(item);
            grouped.set(key, group);
        }
        const groups = [...grouped.values()].sort((left, right) => left.source.name.localeCompare(right.source.name));
        const selectedSourceId = String(open.skillSourceId ?? '').toUpperCase();
        let selectors = '';
        for (const group of groups) {
            group.items.sort((left, right) => {
                const leftTier = left.skillTier ?? Number.POSITIVE_INFINITY;
                const rightTier = right.skillTier ?? Number.POSITIVE_INFINITY;
                if (leftTier !== rightTier) return leftTier - rightTier;
                const leftHasPowerLevel = left.powerLevel !== undefined;
                const rightHasPowerLevel = right.powerLevel !== undefined;
                if (leftHasPowerLevel !== rightHasPowerLevel) return leftHasPowerLevel ? -1 : 1;
                if (leftHasPowerLevel && rightHasPowerLevel && left.powerLevel !== right.powerLevel) {
                    return right.powerLevel! - left.powerLevel!;
                }
                return left.name.localeCompare(right.name);
            });
            const iconKey = group.source.id === 'skill' ? (group.source.type || 'skill') : group.source.id;
            const active = group.source.id.toUpperCase() === selectedSourceId;
            selectors += `<button type="button" class="ft-skill-ability-source${active ? ' is-active' : ''}" data-ft-skill-source="${escapeHtml(group.source.id)}"`
                + ` data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="${escapeHtml(group.source.name)}"`
                + ` onclick="ftActionBarSelectSkillAbilitySource('${escapeHtml(casterId)}','${escapeHtml(group.source.id)}')">${iconMap(iconKey, 22, 22, false)}</button>`;
        }
        if (selectors === '') return '';
        const selected = groups.find((group) => group.source.id.toUpperCase() === selectedSourceId);
        let abilityRow = '';
        if (selected) {
            let medallions = '';
            for (const item of selected.items) {
                const button = generateAbilityButton(item.nest, caster, { iconSize: 'm', source: item.source, consumeNestedUnitPerUse: item.consumeNested === true, bootstrapTooltip: true, barChoosesAmount: true, showChargePill: true });
                if (button) medallions += `<span class="ft-ability-icon">${button}</span>`;
            }
            if (medallions !== '') abilityRow = `<div class="ft-skill-ability-runes ft-ability-iconstrip ft-ai--m">${medallions}</div>`;
        }
        const title = selected ? `Skill Abilities · ${selected.source.name}` : 'Skill Abilities · Choose a Skill';
        const header = `<div class="ft-ability-flyout__head"><span class="ft-ability-flyout__title">${escapeHtml(title)}</span></div>`;
        return `<div class="ft-ability-flyout__inner ft-ability-flyout__inner--skill${selected ? ' has-source-selection' : ''}">${header}${abilityRow}<div class="ft-skill-ability-sources${selected ? ' has-selection' : ''}">${selectors}</div></div>`;
    }
    let items = itemsForCat(caster, cat);
    items = items.slice().sort(open.sort === 'level'
        ? (a, b) => (a.level - b.level) || a.name.localeCompare(b.name)
        : (a, b) => a.name.localeCompare(b.name));
    let meds = '';
    for (const it of items) {
        const btn = generateAbilityButton(it.nest, caster, { iconSize: 'm', spellType: it.spellType, source: it.source, consumeNestedUnitPerUse: it.consumeNested === true, bootstrapTooltip: true, barChoosesAmount: true, showChargePill: true });
        if (btn) meds += `<span class="ft-ability-icon">${btn}</span>`;
    }
    if (meds === '') return '';
    const cid = escapeHtml(casterId);
    const sortBtn = (mode: 'alpha' | 'level', label: string) =>
        `<button type="button" class="ft-island-sort${open.sort === mode ? ' is-active' : ''}" onclick="ftActionBarIslandSort('${cid}','${mode}')">${label}</button>`;
    const header = `<div class="ft-ability-flyout__head"><span class="ft-ability-flyout__title">${escapeHtml(cat.label)}</span>`
        + `<span class="ft-ability-flyout__sorts">${sortBtn('alpha', 'A–Z')}${sortBtn('level', 'Lvl')}</span></div>`;
    return `<div class="ft-ability-flyout__inner">${header}<div class="ft-ability-flyout__strip ft-ability-iconstrip ft-ai--m">${meds}</div></div>`;
}
